import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import TenantLayout from "./components/TenantLayout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Properties from "./pages/Properties";
import Tenants from "./pages/Tenants";
import Receipts from "./pages/Receipts";
import Kostenstellen from "./pages/Kostenstellen";
import Transactions from "./pages/Transactions";
import Jahresabrechnung from "./pages/Jahresabrechnung";
import AdminMessages from "./pages/AdminMessages";
import AIAuditLog from "./pages/AIAuditLog";
import SuperAdminLayout from "./components/SuperAdminLayout";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminMandanten from "./pages/SuperAdminMandanten";
import SuperAdminKIAbrechnung from "./pages/SuperAdminKIAbrechnung";
import SuperAdminSicherheitsAudit from "./pages/SuperAdminSicherheitsAudit";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import TenantRegister from "./pages/TenantRegister";
import TenantDashboard from "./pages/TenantDashboard";
import TenantAbrechnung from "./pages/TenantAbrechnung";
import TenantProfile from "./pages/TenantProfile";
import TenantMessages from "./pages/TenantMessages";
import IdleTimeoutManager from "./components/IdleTimeoutManager";
import Mieteinnahmen from "./pages/Mieteinnahmen";
import GewinnVerlust from "./pages/GewinnVerlust";
import Settings from "./pages/Settings";
import Mahnungen from "./pages/Mahnungen";
import TenantDocuments from "./pages/TenantDocuments";
import Zaehler from "./pages/Zaehler";
import TenantZaehler from "./pages/TenantZaehler";
import HVAbrechnung from "./pages/HVAbrechnung";
import Compliance from "./pages/Compliance";
import Impressum from "./pages/legal/Impressum";
import Datenschutz from "./pages/legal/Datenschutz";
import AGB from "./pages/legal/AGB";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
    </div>
  );
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'tenant') return <Navigate to="/portal" replace />;
  if (user.force_password_change) return <Navigate to="/force-password-change" replace />;
  return <Layout>{children}</Layout>;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin') return <Navigate to="/" replace />;
  if (user.force_password_change) return <Navigate to="/force-password-change" replace />;
  return <SuperAdminLayout>{children}</SuperAdminLayout>;
}

function ForcePwRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.force_password_change) {
    return <Navigate to={user.role === 'super_admin' ? '/super-admin' : user.role === 'tenant' ? '/portal' : '/'} replace />;
  }
  return children;
}

function TenantRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'tenant') return <Navigate to="/" replace />;
  return <TenantLayout>{children}</TenantLayout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) {
    return <Navigate to={user.role === 'tenant' ? '/portal' : '/'} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <>
      <IdleTimeoutManager />
      <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/portal/register" element={<PublicRoute><TenantRegister /></PublicRoute>} />

      {/* Legal Pages – öffentlich erreichbar */}
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<AGB />} />

      {/* Admin Routes */}
      <Route path="/" element={<AdminRoute><Dashboard /></AdminRoute>} />
      <Route path="/properties" element={<AdminRoute><Properties /></AdminRoute>} />
      <Route path="/tenants" element={<AdminRoute><Tenants /></AdminRoute>} />
      <Route path="/mieteinnahmen" element={<AdminRoute><Mieteinnahmen /></AdminRoute>} />
      <Route path="/gewinn-verlust" element={<AdminRoute><GewinnVerlust /></AdminRoute>} />
      <Route path="/receipts" element={<AdminRoute><Receipts /></AdminRoute>} />
      <Route path="/kostenstellen" element={<AdminRoute><Kostenstellen /></AdminRoute>} />
      <Route path="/transactions" element={<AdminRoute><Transactions /></AdminRoute>} />
      <Route path="/jahresabrechnung" element={<AdminRoute><Jahresabrechnung /></AdminRoute>} />
      <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
      <Route path="/mahnungen" element={<AdminRoute><Mahnungen /></AdminRoute>} />
      <Route path="/zaehler" element={<AdminRoute><Zaehler /></AdminRoute>} />
      <Route path="/hv-abrechnung" element={<AdminRoute><HVAbrechnung /></AdminRoute>}/>
      <Route path="/messages" element={<AdminRoute><AdminMessages /></AdminRoute>} />
      <Route path="/ki-protokoll" element={<AdminRoute><AIAuditLog /></AdminRoute>} />

      {/* Super-Admin Routes */}
      <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
      <Route path="/super-admin/mandanten" element={<SuperAdminRoute><SuperAdminMandanten /></SuperAdminRoute>} />
      <Route path="/super-admin/ki-abrechnung" element={<SuperAdminRoute><SuperAdminKIAbrechnung /></SuperAdminRoute>} />
      <Route path="/super-admin/sicherheits-audit" element={<SuperAdminRoute><SuperAdminSicherheitsAudit /></SuperAdminRoute>} />
      <Route path="/super-admin/compliance" element={<SuperAdminRoute><Compliance /></SuperAdminRoute>} />
      <Route path="/force-password-change" element={<ForcePwRoute><ForcePasswordChange /></ForcePwRoute>} />

      {/* Tenant Portal Routes */}
      <Route path="/portal" element={<TenantRoute><TenantDashboard /></TenantRoute>} />
      <Route path="/portal/abrechnung" element={<TenantRoute><TenantAbrechnung /></TenantRoute>} />
      <Route path="/portal/profile" element={<TenantRoute><TenantProfile /></TenantRoute>} />
      <Route path="/portal/messages" element={<TenantRoute><TenantMessages /></TenantRoute>} />
      <Route path="/portal/dokumente" element={<TenantRoute><TenantDocuments /></TenantRoute>} />
      <Route path="/portal/zaehler" element={<TenantRoute><TenantZaehler /></TenantRoute>} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
